import type { ClientId } from '../constants/clientId';
import { CLIENT_ID_HEADER, CLIENT_VERSION_HEADER } from '../constants/clientId';

const urlOf = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
};

/**
 * Wrap `window.fetch` so every request to `apiHost` carries the X-Payaca-Client
 * header, and X-Payaca-Client-Version when `clientVersion` is given. Existing
 * values on the call site are preserved. Call once at app boot, before any code
 * makes a fetch.
 */
export const installClientIdFetchInterceptor = ({
  clientId,
  clientVersion,
  apiHost,
}: {
  clientId: ClientId;
  clientVersion?: string;
  apiHost: string;
}): void => {
  if (typeof globalThis.fetch !== 'function') return;
  const originalFetch = globalThis.fetch.bind(globalThis);

  globalThis.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (!urlOf(input).startsWith(apiHost)) {
      return originalFetch(input, init);
    }

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    if (!headers.has(CLIENT_ID_HEADER)) {
      headers.set(CLIENT_ID_HEADER, clientId);
    }
    if (clientVersion && !headers.has(CLIENT_VERSION_HEADER)) {
      headers.set(CLIENT_VERSION_HEADER, clientVersion);
    }

    return originalFetch(input, { ...init, headers });
  };
};
